/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-04-11 18:29:18
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-09-05 16:19:29
 * @FilePath: /src/components/Form/FormInput/index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
'use client';

import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import { EPlatform } from '@/utils/enums/platform-enum';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { InputDelete } from '@zeelool/icon';
import { omit } from 'lodash-es';
import { ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { Control, Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';

import Button, { CustomButtonProps } from '../../Button';
import useHydratedWhenEnabled from '../useHydratedWhenEnabled';

/**
 * Input组件的属性
 */
export type FloatingLabelVariant = 'notched' | 'inside';

export interface InputProps extends Omit<TextFieldProps, 'variant'> {
  /**
   * Label 的浮动方式：
   * - notched：MUI 默认行为，聚焦后浮到输入框边框上
   * - inside：聚焦或有值时缩小并保留在输入框内部
   */
  floatingLabelVariant?: FloatingLabelVariant;
  /**
   * 标题和输入框的布局方式，默认inline，也就是标题和输入框融为一体
   * 如果设置为vertical，那么标题和输入框会垂直排列
   */
  layout?: 'inline' | 'vertical' | 'horizontal';
  /**
   * 是否可清空
   */
  clearable?: boolean;
  /**
   * 输入框的名称
   */
  name?: string;
  /**
   * 输入框的控制器
   */
  control?: Control<any>;
  /**
   * 输入框的规则
   */
  rules?: RegisterOptions;
  /**
   * 按钮的属性
   */
  actionBtnProps?: CustomButtonProps & {
    name: string;
    sdExpProps?: { sdEventName: string; sdEventParams: Record<string, any> };
  };
  /**
   * 自定义输入处理函数，用于在输入过程中处理输入内容
   * 返回值将作为最终的输入值传递给react-hook-form
   */
  formatInput?: (value: string) => string;
  /**
   * 清空按钮点击事件
   */
  onClear?: () => void;
  /**
   * 输入框内尾部的图标，如密码显示/隐藏图标
   */
  endIcon?: React.ReactNode;
  /**
   * 表单项的className
   */
  formItemClassName?: string;
  /**
   * 是否过滤emoji
   */
  isFilterEmoji?: boolean;
  /**
   * 是否走原生的错误提示，因为存在错误文案多行的场景
   * 为了不影响布局的调整，原先的设计是做了一个绝对定位
   * 的错误文案提醒，但是这样在移动端会显示不全，所以需要
   * 走原生的错误提示
   */
  useNativeError?: boolean;
  /**
   * 给错误文本包一层，方便调整样式
   */
  slotErrorText?: (content: ReactNode) => ReactNode;
  /**
   * 是否阻止Enter键事件冒泡，默认true
   */
  preventEnterKeyPropagation?: boolean;
  /**
   * SSR 首帧为空、客户端从缓存恢复为非空值时，挂载后同步修复浮动 Label 和 outline 缺口。
   * 默认关闭，仅供存在客户端缓存回填的表单按需启用。
   */
  shrinkLabelWhenFilled?: boolean;
}

const Input: React.FC<InputProps> = ({
  formItemClassName = 'mb-[32px]',
  size,
  helperText,
  clearable,
  onClear,
  value,
  onChange,
  fullWidth = true,
  slotProps,
  layout = 'inline',
  label,
  name,
  control,
  rules,
  sx,
  endIcon,
  actionBtnProps,
  formatInput,
  isFilterEmoji = true,
  useNativeError = false,
  floatingLabelVariant = 'notched',
  shrinkLabelWhenFilled = false,
  slotErrorText = (content) => content,
  ...props
}) => {
  // 获取表单上下文
  const formContext = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef(0);
  const { trackEvent } = useReporting();
  const { deviceType } = useContext(ResponsiveContext);
  const isHydrated = useHydratedWhenEnabled(shrinkLabelWhenFilled);

  // 判断是否为移动端
  const isMobile = deviceType === EPlatform.WAP;

  // 解决中文输入法在输入过程中避免校验的问题
  // const [isComposing, setIsComposing] = useState(false);
  const [actionBtnRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && actionBtnProps?.sdExpProps) {
      trackEvent(
        {
          eventName: actionBtnProps.sdExpProps.sdEventName,
          properties: actionBtnProps.sdExpProps.sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    }
  }, [inView]);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = e.target.selectionStart!;
  };

  // 处理输入变化，支持格式化/过滤等自定义处理
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldOnChange?: (value: any) => void,
  ) => {
    const rawValue = e.target.value;
    const selectionStart = e.target.selectionStart || 0;

    // 如果提供了formatInput函数，使用它处理输入值
    let processedValue = formatInput ? formatInput(rawValue) : rawValue;
    if (isFilterEmoji) {
      processedValue = processedValue.replace(
        /[^\u0020-\u007E\u00A0-\u00BE\u00C0-\u00FF\u2E80-\uA4CF\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFFEF\u0080-\u009F\u2000-\u201F\r\n]/g,
        '',
      );
    }

    // 如果处理后的值与原始值不同，创建一个新的event对象
    const finalEvent =
      processedValue !== rawValue ? { ...e, target: { ...e.target, value: processedValue } } : e;

    // 计算新的光标位置
    if (processedValue !== rawValue && formatInput) {
      // 在值变化时，保持相对位置
      const ratio = Math.min(1, selectionStart / Math.max(1, rawValue.length));
      cursorPositionRef.current = Math.round(ratio * processedValue.length);
    } else {
      cursorPositionRef.current = selectionStart;
    }

    // 确保光标位置不会超出范围
    cursorPositionRef.current = Math.max(
      0,
      Math.min(cursorPositionRef.current, processedValue.length),
    );

    // 调用外部onChange
    if (onChange) {
      onChange(finalEvent);
    }

    // 更新react-hook-form的值
    if (fieldOnChange) {
      fieldOnChange(processedValue);
    }

    // 立即应用光标位置，而不是等待下一次渲染
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      }
    }, 0);
  };

  // 处理失焦事件
  const handleBlur = useCallback(() => {
    // 手动触发验证
    if (formContext && name) {
      setTimeout(() => {
        formContext.trigger(name);
      }, 0);
    }
  }, [formContext, name]);

  // 处理键盘事件，禁用enter键
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // 根据preventEnterKeyPropagation属性决定是否阻止事件冒泡
        if (props.preventEnterKeyPropagation !== false) {
          e.stopPropagation();
        }
      }
    },
    [props.preventEnterKeyPropagation],
  );

  const shouldRestoreUncontrolledFilledOutline =
    shrinkLabelWhenFilled &&
    isHydrated &&
    floatingLabelVariant === 'notched' &&
    value !== undefined &&
    value !== null &&
    String(value).trim() !== '';
  const renderedUncontrolledValue = shrinkLabelWhenFilled && !isHydrated ? '' : value || '';
  const errorLabelSx = {
    // 保持错误状态下浮动 Label 与地址表单一致的红色。
    '& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-error': {
      color: '#bc1b1b',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-error span': {
      color: '#bc1b1b',
    },
  };
  const insideFloatingLabelSx =
    floatingLabelVariant === 'inside' && label
      ? {
          // 全局主题为旧版外浮 Label 预留了 8px 顶部空间；
          // 新版 Label 位于输入框内部，不再需要这段占位。
          '&.MuiTextField-root': {
            marginTop: 0,
          },
          // 新版输入框保留 MUI 的聚焦/有值判断，只将浮动 Label 移到输入框内部。
          '& .MuiInputLabel-root.MuiFormLabel-root': {
            top: 0,
            // 新版输入框高 50px，按 17px 行高精确垂直居中。
            transform: 'translate(12px, 16.5px) scale(1)',
            transformOrigin: 'top left',
            fontSize: '14px',
            lineHeight: '17px',
          },
          '& .MuiInputLabel-root.MuiFormLabel-root.MuiInputLabel-shrink': {
            transform: 'translate(12px, 6px) scale(1)',
            fontSize: '12px',
            lineHeight: '14px',
          },
          '& .MuiOutlinedInput-input': {
            ...sx?.['& .MuiOutlinedInput-input'],
            paddingLeft: '12px',
            paddingTop: '21px',
            paddingBottom: '6px',
            fontSize: '14px',
            lineHeight: '17px',
          },
          // Label 不再穿过边框，因此关闭 MUI 默认的 outline 缺口。
          '& .MuiOutlinedInput-notchedOutline legend': {
            maxWidth: '0 !important',
          },
          // 新版错误提示与输入框内容保持 12px 左右对齐，并与输入框间隔 8px。
          '& .MuiFormHelperText-root': {
            margin: '8px 0 0',
            fontSize: '12px',
            lineHeight: '14px',
          },
        }
      : {};

  // 如果没有control，直接返回基本的TextField
  if (!control || !name) {
    return (
      <TextField
        inputRef={inputRef}
        size={size}
        error={!!props.error}
        helperText={helperText}
        autoComplete="off"
        fullWidth={fullWidth}
        value={renderedUncontrolledValue}
        onChange={onChange}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        label={label}
        slotProps={
          shrinkLabelWhenFilled
            ? {
                ...slotProps,
                inputLabel: {
                  ...(shouldRestoreUncontrolledFilledOutline ? { shrink: true } : {}),
                  ...(slotProps?.inputLabel || {}),
                },
                input: {
                  ...(shouldRestoreUncontrolledFilledOutline ? { notched: true } : {}),
                  ...(slotProps?.input || {}),
                },
              }
            : slotProps
        }
        sx={{
          ...errorLabelSx,
          ...sx,
          ...(floatingLabelVariant === 'inside'
            ? {
                '& .MuiOutlinedInput-root.MuiInputBase-root': {
                  borderRadius: '6px',
                },
              }
            : {}),
          ...insideFloatingLabelSx,
        }}
        {...omit(props, 'preventEnterKeyPropagation')}
      />
    );
  }

  // 如果是受控组件，使用Controller
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        // 构建endAdornment
        const getEndAdornment = () => {
          // 清空按钮
          const clearButton =
            clearable && field.value && isMobile ? (
              <IconButton
                size="small"
                onClick={() => {
                  // 直接在这里处理清空逻辑
                  field.onChange(''); // 更新Controller值
                  if (name && formContext) {
                    formContext.setValue(name, ''); // 更新表单值
                    setTimeout(() => formContext.trigger(name), 0);
                  }
                  if (onClear) onClear();
                }}
                edge="end"
                aria-label="clear input"
              >
                {/* <DeleteCircleFilled className="text-[20px] text-auxiliary-text" /> */}
                <InputDelete className="text-[16px] pr-[2px]" />
              </IconButton>
            ) : null;

          // 如果有actionBtnProps，创建按钮作为endAdornment
          const actionButton = actionBtnProps ? (
            <InputAdornment
              position="end"
              sx={{ margin: 0, padding: 0, height: '100%', maxHeight: 'none' }}
            >
              <Button
                className="!h-full lg:w-[100px]"
                variant="contained"
                sx={{
                  '& .MuiIconButton-root': {
                    marginRight: '-8px',
                  },
                  borderRadius: '0 4px 4px 0',
                  // 覆盖输入框边框
                  marginRight: '-1px',
                  marginTop: '-1px',
                  marginBottom: '-1px',
                  height: 'calc(100% + 2px)', // 补偿边框
                  // 确保边框不会干扰
                  position: 'relative',
                  zIndex: 1,
                }}
                ref={actionBtnRef}
                {...omit(actionBtnProps, 'sdExpProps')}
              >
                {actionBtnProps.name}
              </Button>
            </InputAdornment>
          ) : null;

          // 如果既没有清空按钮、尾部图标也没有操作按钮，返回 undefined
          if (!clearButton && !endIcon && !actionButton) {
            return undefined;
          }

          // 返回组合的 endAdornment - 如果有actionButton，只返回actionButton
          if (actionButton) {
            return actionButton;
          }

          // 否则返回常规endAdornment
          return (
            <InputAdornment position="end">
              {clearButton}
              {endIcon}
            </InputAdornment>
          );
        };
        // TODO: 对于复合组件使用FormInput的错误文案，目前没跑通，先临时用这种方式
        // 取两处的值，期望是自己的这个值可以的
        const finalError = props.error || !!error;
        const finalHelperText = props.error ? helperText : error?.message || helperText;
        const shouldRestoreFilledOutline =
          shrinkLabelWhenFilled &&
          isHydrated &&
          floatingLabelVariant === 'notched' &&
          field.value !== undefined &&
          field.value !== null &&
          String(field.value).trim() !== '';
        const renderedFieldValue = shrinkLabelWhenFilled && !isHydrated ? '' : field.value || '';

        return (
          <div
            className={`relative ${formItemClassName} ${layout === 'horizontal' ? 'flex items-center justify-start gap-12 !mb-0' : ''}`}
          >
            {/* 标题 */}
            {(layout === 'vertical' || layout === 'horizontal') && label && (
              <label
                className={`block text-[14px] md:text-[16px] text-primary-text font-br mb-[4px] text-nowrap ${
                  layout === 'horizontal' ? '!mb-0' : ''
                }`}
              >
                {label}
              </label>
            )}

            {/* 输入框与按钮的容器 */}
            <div className={`relative flex items-baseline`}>
              <TextField
                inputRef={inputRef}
                size={size}
                error={!!finalError}
                helperText={
                  !finalError || (finalError && useNativeError) ? finalHelperText : undefined
                }
                autoComplete="off"
                fullWidth={fullWidth}
                // onCompositionStart={() => setIsComposing(true)}
                // onCompositionEnd={(e) => {
                //   setIsComposing(false);
                //   // Trigger the change handler manually with the final composed value
                //   handleInputChange(e as any, field.onChange);
                // }}
                className={`${layout === 'horizontal' ? 'w-[400px] !mt-0' : ''}`}
                value={renderedFieldValue}
                onChange={(e) => {
                  handleInputChange(e, field.onChange);
                }}
                onSelect={handleSelect}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                label={layout === 'vertical' || layout === 'horizontal' ? undefined : label}
                sx={{
                  ...errorLabelSx,
                  ...sx,
                  '& .MuiOutlinedInput-root': {
                    paddingRight: isMobile ? '12px' : '16px', // PC端16px，WAP端12px
                    ...sx?.['& .MuiOutlinedInput-root'], // 让外部传入的样式覆盖默认样式
                  },
                  ...(actionBtnProps
                    ? {
                        '& .MuiOutlinedInput-root': {
                          paddingRight: 0, // 移除右边距，让按钮紧贴
                        },
                        '& .MuiInputAdornment-root': {
                          margin: 0,
                          height: '100%',
                          maxHeight: 'none',
                        },
                      }
                    : {}),
                  ...(floatingLabelVariant === 'inside'
                    ? {
                        '& .MuiOutlinedInput-root.MuiInputBase-root': {
                          borderRadius: '6px',
                        },
                      }
                    : {}),
                  ...insideFloatingLabelSx,
                }}
                slotProps={{
                  ...slotProps,
                  ...(shrinkLabelWhenFilled
                    ? {
                        inputLabel: {
                          ...(shouldRestoreFilledOutline ? { shrink: true } : {}),
                          ...(slotProps?.inputLabel || {}),
                        },
                      }
                    : {}),
                  input: {
                    endAdornment: getEndAdornment(),
                    ...(shouldRestoreFilledOutline ? { notched: true } : {}),
                    ...(slotProps?.input || {}),
                  },
                }}
                {...omit(props, 'preventEnterKeyPropagation')}
              />

              {/* 使用CSS transitions实现错误信息的动画显示 */}
              {!useNativeError ? (
                <div
                  className={`
                    ${finalHelperText ? 'form-error-text' : ''} absolute left-0 top-full text-error text-[12px] pt-[4px]
                    transition-all duration-200 ease-in-out
                    ${finalError ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'}
                  `}
                >
                  {slotErrorText(finalHelperText)}
                </div>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
};

Input.displayName = 'Input';

export default Input;
